import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Search,
  MessageSquare,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Send,
  Calendar,
  Tag,
  Plus,
  X,
} from "lucide-react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import "../../../../assets/css/patient/patientlayouts/layoutpages/SupportTickets.css";

const CATEGORIES = ["Booking Issue", "Technical Issue", "Payment Issue", "Account Issue", "Emergency", "Other"];

// TODO: adjust base URL to match your Laravel backend
const API_BASE_URL = 'https://backend.bizdevit.com/api';
// axios instance with auth token attached automatically
// TODO: change "patient_token" if your app stores the token under a different key
const api = axios.create({ baseURL: API_BASE_URL });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("patient_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTicket, setNewTicket] = useState({ category: CATEGORIES[0], subject: "", description: "" });
  const itemsPerPage = 5;

  // Maps a Laravel ticket record (snake_case) to the shape this page uses
  const mapTicket = (t) => ({
    id: t.ticket_number,
    dbId: t.id,
    subject: t.subject,
    category: t.category,
    status: t.status,
    createdAt: new Date(t.created_at).toLocaleString(),
    updatedAt: new Date(t.updated_at).toLocaleString(),
    description: t.description,
    replies: (t.replies || []).map((r) => ({
      from: r.sender_type === "patient" ? "You" : "Support Team",
      message: r.message,
      time: new Date(r.created_at).toLocaleString(),
    })),
  });

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get("/tickets");
      const list = res.data?.data?.data || res.data?.data || [];
      setTickets(list.map(mapTicket));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const filteredTickets = tickets
    .filter((t) => activeTab === "All" || t.status === activeTab)
    .filter(
      (t) =>
        t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTickets = filteredTickets.slice(startIndex, startIndex + itemsPerPage);

  const stats = {
    total: tickets.length,
    open: tickets.filter((t) => t.status === "Open").length,
    inProgress: tickets.filter((t) => t.status === "In Progress").length,
    resolved: tickets.filter((t) => t.status === "Resolved").length,
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Open":
        return "st-status-open";
      case "In Progress":
        return "st-status-progress";
      case "Resolved":
        return "st-status-resolved";
      case "Closed":
        return "st-status-closed";
      default:
        return "";
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      Swal.fire({
        title: "Empty Message",
        text: "Please type a message before sending.",
        icon: "warning",
        confirmButtonColor: "#ea580c",
      });
      return;
    }

    try {
      await api.post(`/tickets/${selectedTicket.dbId}/reply`, { message: replyMessage });

      const newReply = { from: "You", message: replyMessage, time: new Date().toLocaleString() };
      const updatedTickets = tickets.map((t) =>
        t.id === selectedTicket.id
          ? { ...t, replies: [...t.replies, newReply], updatedAt: new Date().toLocaleString() }
          : t
      );

      setTickets(updatedTickets);
      setSelectedTicket({ ...selectedTicket, replies: [...selectedTicket.replies, newReply] });
      setReplyMessage("");

      Swal.fire({
        title: "Reply Sent!",
        text: "Your message has been sent to our support team.",
        icon: "success",
        confirmButtonColor: "#16a34a",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        title: "Reply Failed",
        text: err.response?.data?.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#ea580c",
      });
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      Swal.fire({
        title: "Missing Information",
        text: "Please fill in the subject and description.",
        icon: "warning",
        confirmButtonColor: "#ea580c",
      });
      return;
    }

    try {
      const res = await api.post("/tickets", {
        category: newTicket.category,
        subject: newTicket.subject,
        description: newTicket.description,
      });

      const created = mapTicket(res.data.data);

      setTickets([created, ...tickets]);
      setShowCreateModal(false);
      setNewTicket({ category: CATEGORIES[0], subject: "", description: "" });
      setActiveTab("All");
      setCurrentPage(1);

      Swal.fire({
        title: "Ticket Submitted!",
        html: `Your ticket <strong>${created.id}</strong> has been created. Our team will respond soon.`,
        icon: "success",
        confirmButtonColor: "#1e3a5f",
        timer: 2200,
      });
    } catch (err) {
      Swal.fire({
        title: "Could Not Submit Ticket",
        text: err.response?.data?.message || "Something went wrong. Please try again.",
        icon: "error",
        confirmButtonColor: "#ea580c",
      });
    }
  };

  return (
    <div className="st-wrapper">
      <div className="st-container">
        {/* Header */}
        <div className="st-header">
          <div>
            <h1 className="st-title">Support Tickets</h1>
            <p className="st-subtitle">Track your support requests and get help from our team</p>
          </div>
          <button className="st-btn-create" onClick={() => setShowCreateModal(true)}>
            <Plus size={18} />
            Raise New Ticket
          </button>
        </div>

        {/* Stats Cards */}
        <div className="st-stats-grid">
          <div className="st-stat-card">
            <div className="st-stat-icon st-stat-blue">
              <MessageSquare size={24} />
            </div>
            <div>
              <div className="st-stat-value">{stats.total}</div>
              <div className="st-stat-label">My Tickets</div>
            </div>
          </div>
          <div className="st-stat-card">
            <div className="st-stat-icon st-stat-orange">
              <Clock size={24} />
            </div>
            <div>
              <div className="st-stat-value">{stats.open}</div>
              <div className="st-stat-label">Open</div>
            </div>
          </div>
          <div className="st-stat-card">
            <div className="st-stat-icon st-stat-purple">
              <AlertCircle size={24} />
            </div>
            <div>
              <div className="st-stat-value">{stats.inProgress}</div>
              <div className="st-stat-label">In Progress</div>
            </div>
          </div>
          <div className="st-stat-card">
            <div className="st-stat-icon st-stat-green">
              <CheckCircle size={24} />
            </div>
            <div>
              <div className="st-stat-value">{stats.resolved}</div>
              <div className="st-stat-label">Resolved</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="st-main-card">
          {/* Tabs */}
          <div className="st-tabs">
            {["All", "Open", "In Progress", "Resolved", "Closed"].map((tab) => (
              <button
                key={tab}
                className={`st-tab ${activeTab === tab ? "st-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="st-filters">
            <div className="st-search-wrapper">
              <Search size={18} className="st-search-icon" />
              <input
                type="text"
                placeholder="Search your tickets..."
                className="st-search-input"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Content Grid */}
          <div className="st-content-grid">
            {/* Left: Tickets List */}
            <div className="st-list-section">
              <h3 className="st-section-title">My Tickets</h3>

              {loading ? (
                <div className="st-empty-state">
                  <p>Loading your tickets...</p>
                </div>
              ) : filteredTickets.length === 0 ? (
                <div className="st-empty-state">
                  <MessageSquare size={48} />
                  <p>No tickets found</p>
                  <button className="st-btn-empty-create" onClick={() => setShowCreateModal(true)}>
                    <Plus size={16} />
                    Raise your first ticket
                  </button>
                </div>
              ) : (
                <>
                  <div className="st-table-container">
                    <table className="st-table">
                      <thead>
                        <tr>
                          <th className="st-th">Ticket ID</th>
                          <th className="st-th">Subject</th>
                          <th className="st-th">Category</th>
                          <th className="st-th">Status</th>
                          <th className="st-th">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTickets.map((ticket) => (
                          <tr key={ticket.id} className="st-tr">
                            <td className="st-td">
                              <span className="st-ticket-id">{ticket.id}</span>
                            </td>
                            <td className="st-td">
                              <div className="st-subject-cell">
                                <span className="st-subject">{ticket.subject}</span>
                                <span className="st-user">{ticket.createdAt}</span>
                              </div>
                            </td>
                            <td className="st-td">
                              <span className="st-category-badge">{ticket.category}</span>
                            </td>
                            <td className="st-td">
                              <span className={`st-status-badge ${getStatusColor(ticket.status)}`}>
                                {ticket.status}
                              </span>
                            </td>
                            <td className="st-td">
                              <button className="st-btn-view" onClick={() => setSelectedTicket(ticket)}>
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="st-pagination">
                    <div className="st-pagination-info">
                      Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredTickets.length)} of{" "}
                      {filteredTickets.length} tickets
                    </div>
                    <div className="st-pagination-controls">
                      <button
                        className="st-pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                          return (
                            <button
                              key={page}
                              className={`st-pagination-number ${currentPage === page ? "st-pagination-number-active" : ""}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return (
                            <span key={page} style={{ padding: "0 8px", color: "#94a3b8" }}>
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}

                      <button
                        className="st-pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right: Ticket Details */}
            <div className="st-details-panel">
              {selectedTicket ? (
                <div className="st-details-content">
                  <div className="st-details-header">
                    <h2 className="st-details-title">{selectedTicket.subject}</h2>
                    <span className="st-ticket-id-badge">{selectedTicket.id}</span>
                  </div>

                  <div className="st-ticket-meta">
                    <div className="st-meta-item">
                      <Calendar size={16} />
                      <div>
                        <div className="st-meta-label">Created</div>
                        <div className="st-meta-value">{selectedTicket.createdAt}</div>
                      </div>
                    </div>
                    <div className="st-meta-item">
                      <Tag size={16} />
                      <div>
                        <div className="st-meta-label">Category</div>
                        <div className="st-meta-value">{selectedTicket.category}</div>
                      </div>
                    </div>
                    <div className="st-meta-item">
                      <AlertCircle size={16} />
                      <div>
                        <div className="st-meta-label">Status</div>
                        <span className={`st-status-badge ${getStatusColor(selectedTicket.status)}`}>
                          {selectedTicket.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="st-description-section">
                    <h3 className="st-section-subtitle">Description</h3>
                    <p className="st-description">{selectedTicket.description}</p>
                  </div>

                  <div className="st-replies-section">
                    <h3 className="st-section-subtitle">Conversation ({selectedTicket.replies.length})</h3>
                    <div className="st-replies-list">
                      {selectedTicket.replies.length === 0 ? (
                        <p className="st-no-replies">No replies yet. Our support team will respond soon.</p>
                      ) : (
                        selectedTicket.replies.map((reply, index) => (
                          <div
                            key={index}
                            className={`st-reply-item ${reply.from === "You" ? "st-reply-item-mine" : ""}`}
                          >
                            <div className="st-reply-header">
                              <span className="st-reply-from">{reply.from}</span>
                              <span className="st-reply-time">{reply.time}</span>
                            </div>
                            <p className="st-reply-message">{reply.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {selectedTicket.status !== "Closed" ? (
                    <div className="st-reply-form">
                      <textarea
                        className="st-reply-textarea"
                        placeholder="Type your message here..."
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        rows={4}
                      />
                      <button className="st-btn-send" onClick={handleSendReply}>
                        <Send size={18} />
                        Send Message
                      </button>
                    </div>
                  ) : (
                    <p className="st-closed-note">This ticket is closed. Raise a new ticket if the issue persists.</p>
                  )}
                </div>
              ) : (
                <div className="st-no-selection">
                  <MessageSquare size={48} />
                  <p>Select a ticket to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Ticket Modal */}
      {showCreateModal && (
        <div className="st-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="st-modal" onClick={(e) => e.stopPropagation()}>
            <div className="st-modal-header">
              <h2>Raise New Ticket</h2>
              <button className="st-modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="st-modal-body">
              <div className="st-form-group">
                <label className="st-form-label">Category</label>
                <select
                  className="st-form-select"
                  value={newTicket.category}
                  onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                >
                  {CATEGORIES.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              <div className="st-form-group">
                <label className="st-form-label">Subject</label>
                <input
                  type="text"
                  className="st-form-input"
                  placeholder="Briefly describe the issue"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                />
              </div>

              <div className="st-form-group">
                <label className="st-form-label">Description</label>
                <textarea
                  className="st-form-textarea"
                  placeholder="Explain the issue in detail..."
                  rows={5}
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                />
              </div>
            </div>

            <div className="st-modal-footer">
              <button className="st-btn-cancel" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button className="st-btn-submit" onClick={handleCreateTicket}>
                Submit Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;